<template>
  <div
    class="SidebarSubcategoriesPanel"
    :class="{ 'SidebarSubcategoriesPanel--unfolded': unfolded }"
  >
    <div class="SidebarSubcategoriesPanel__inner">
      <div v-if="unfolded" class="SidebarSubcategoriesPanel__search">
        <Autocomplete
          :key="searchAutocompleteKey"
          v-model="selectedSearchValue"
          :items="searchItems"
          item-value="id"
          :item-title="getSearchItemLabel"
          :placeholder="
            $t(
              'myMap.rightSidebar.searchCategoryPlaceholder',
              'Rechercher un domaine, sous-domaine, type d\'ouvrage, donnée ou infrastructure (nom, attribut, valeur)…',
            )
          "
          :no-data-text="$t('myMap.rightSidebar.searchNoData', 'Aucun résultat')"
          clearable
          density="compact"
          inline-options
          @update:model-value="onSearchSelect"
        >
          <template #item="{ item }">
            <div class="SidebarSubcategoriesPanel__searchResult">
              <span class="SidebarSubcategoriesPanel__searchResultLabel">
                <span class="SidebarSubcategoriesPanel__searchResultName">{{ item.name }}</span>
                <span class="SidebarSubcategoriesPanel__searchResultCategory">{{ item.categoryName }}</span>
              </span>
            </div>
          </template>
        </Autocomplete>
      </div>
      <template v-if="unfolded && orderedCategories.length > 0">
        <MyMapOsmCategoryDetails
          v-for="category in orderedCategories"
          :key="category.id"
          :category="category"
          :expanded="expandedCategoryId === category.id"
          :initial-sous-domaine-id="expandedCategoryId === category.id ? pendingSousDomaineId : null"
          @toggle="(expanded) => onCategoryToggle(category.id, expanded)"
        />
      </template>
      <div v-else-if="unfolded" class="SidebarSubcategoriesPanel__hint">
        {{
          $t('myMap.rightSidebar.searchHint', 'Recherchez ou sélectionnez une catégorie ci-dessus.')
        }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AtlasCategory } from '@/models/interfaces/map/AtlasCategory'
import { fondMatchesCategory } from '@/services/map/MapStylesService'
import { formatDomaineLib, formatSousDomaineLib } from '@/services/utils/UtilsService'
import { libelleDynamique } from '@/utils/libelleDynamique'
import { InfrastructureService, type AttributeValueItem } from '@/services/infrastructure/InfrastructureService'
import { AtlasCategoriesService } from '@/services/map/osm/AtlasCategoriesService'
import MyMapOsmCategoryDetails from '@/views/map/components/OsmCategories/MyMapOsmCategoryDetails.vue'
import Autocomplete from '@/components/ui/Autocomplete.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import { useI18n } from 'vue-i18n'
import { computed, provide, ref, watch } from 'vue'

/** aglStatut value marking a rejected attribute value (see AdminInfrastructureFicheDataPanel) — excluded from search. */
const AGL_STATUT_REJECTED = 2

interface GeoportalSearchItem {
  type: 'category' | 'sousDomaine' | 'typeOuvrage' | 'donnee' | 'fond' | 'infrastructure'
  id: string
  /** Full label used for autocomplete filtering (category › name) */
  displayLabel: string
  /** The subcategory, type d'ouvrage, donnée, fond layer or infrastructure name shown prominently in the result */
  name: string
  /** The parent category (or sous-domaine/attribut, for nested results) name shown as secondary context */
  categoryName: string
  categoryId: number
  sousDomaineId?: number | string
  /** Set for type 'infrastructure': the infrastructure's ift_id, used to open its fiche directly. */
  infrastructureId?: string | number
}

const props = defineProps<{
  unfolded: boolean
  osmCategories: AtlasCategory[]
}>()

const { t } = useI18n()
const mapStore = useMyMapStore()
const hideDetails = ref(false)
provide('hideDetails', hideDetails)
const expandedCategoryId = ref<number | null>(null)
/** Sous-domaine to auto-expand once its parent category is expanded (set when a search result targets it). */
const pendingSousDomaineId = ref<number | string | null>(null)

const selectedSearchValue = ref<string | null>(null)

/** Attribute values for all infrastructures, loaded lazily the first time the panel unfolds so the search bar can reach infrastructure attributes, not just the domain/type d'ouvrage tree. */
const infrastructureAttributeValues = ref<AttributeValueItem[]>([])
let infrastructureSearchLoadPromise: Promise<void> | null = null
function ensureInfrastructureSearchDataLoaded(): void {
  if (infrastructureSearchLoadPromise) return
  infrastructureSearchLoadPromise = InfrastructureService.getAllAttributeValues()
    .then((rows) => {
      infrastructureAttributeValues.value = rows
    })
    .catch(() => {
      infrastructureAttributeValues.value = []
    })
}
watch(
  () => props.unfolded,
  (unfolded) => {
    if (unfolded) ensureInfrastructureSearchDataLoaded()
  },
  { immediate: true },
)

/** Renders the first non-empty value column of an attribute value row as display text. */
function formatAttributeValueText(row: AttributeValueItem): string {
  if (row.aglValTxtcourt != null && row.aglValTxtcourt !== '') return row.aglValTxtcourt
  if (row.aglValNum != null) return String(row.aglValNum)
  if (row.aglValDate != null) return row.aglValDate
  if (row.aglValBool != null) return row.aglValBool ? t('common.yes', 'Oui') : t('common.no', 'Non')
  return ''
}

const searchAutocompleteKey = ref(0)
watch(
  () => props.osmCategories,
  () => {
    searchAutocompleteKey.value += 1
    selectedSearchValue.value = null
    const selectedId = mapStore.activeOsmCategory.rightPanel.categoryId
    if (typeof selectedId === 'number' && props.osmCategories.some((c) => c.id === selectedId)) {
      expandedCategoryId.value = selectedId
      return
    }
    expandedCategoryId.value = null
  },
  { deep: true, immediate: true },
)

watch(
  () => mapStore.activeOsmCategory.rightPanel.categoryId,
  (categoryId) => {
    if (typeof categoryId === 'number' && props.osmCategories.some((c) => c.id === categoryId)) {
      expandedCategoryId.value = categoryId
      return
    }
    expandedCategoryId.value = null
  },
  { immediate: true },
)

const orderedCategories = computed(() => {
  return [...props.osmCategories].sort((a, b) => a.position - b.position)
})

const searchItems = computed((): GeoportalSearchItem[] => {
  const items: GeoportalSearchItem[] = []
  for (const category of orderedCategories.value) {
    // Raw label kept for fondMatchesCategory (matching logic) — only the display copy is cleaned up.
    const categoryLabel = category.atlas_category ?? category.name
    const categoryDisplayLabel = formatDomaineLib(categoryLabel)

    items.push({
      type: 'category',
      id: `category-${category.id}`,
      displayLabel: categoryDisplayLabel,
      name: categoryDisplayLabel,
      categoryName: t('myMap.rightSidebar.searchResultTypeDomaine', 'Domaine'),
      categoryId: category.id,
    })

    // Donnees attached directly to the category (no sous-domaine level, "no sous-domaine" format).
    for (const donnee of category.donnees ?? []) {
      const donneeName = donnee.name ?? donnee.nameFr ?? donnee.nameEn ?? String(donnee.id)
      items.push({
        type: 'donnee',
        id: `donnee-category-${category.id}-${donnee.id}`,
        displayLabel: `${categoryDisplayLabel} › ${donneeName}`,
        name: donneeName,
        categoryName: categoryDisplayLabel,
        categoryId: category.id,
      })
    }

    const apiFonds = category.fondLayers ?? []
    if (apiFonds.length > 0) {
      for (const fond of apiFonds) {
        const fondLabel = fond.label ?? fond.name ?? String(fond.id)
        const resolvedStyleId = fond.styleId
          ?? mapStore.fondLayerStyles.find((s) => (s.metadata?.label ?? s.id) === fondLabel)?.id
          ?? fond.name
          ?? String(fond.id)
        items.push({
          type: 'fond',
          id: `fond-${resolvedStyleId}`,
          displayLabel: `${categoryDisplayLabel} › ${fondLabel}`,
          name: fondLabel,
          categoryName: categoryDisplayLabel,
          categoryId: category.id,
        })
      }
    } else {
      for (const fond of mapStore.fondLayerStyles.filter((f) => fondMatchesCategory(f, categoryLabel))) {
        const fondLabel = fond.metadata?.label ?? fond.id
        items.push({
          type: 'fond',
          id: `fond-${fond.id}`,
          displayLabel: `${categoryDisplayLabel} › ${fondLabel}`,
          name: fondLabel,
          categoryName: categoryDisplayLabel,
          categoryId: category.id,
        })
      }
    }
    const sousDomaines = [...(category.sousDomaines ?? [])].sort((a, b) => a.order - b.order)
    for (const sousDomaine of sousDomaines) {
      // libelleDynamique retombe sur le français quand la traduction anglaise manque, là où
      // `isFr ? nameFr : nameEn` affichait un libellé vide.
      const sousDomaineName = formatSousDomaineLib(
        libelleDynamique(sousDomaine.name ?? sousDomaine.nameFr, sousDomaine.nameEn),
      )
      items.push({
        type: 'sousDomaine',
        id: `sousdomaine-${sousDomaine.id}`,
        displayLabel: `${categoryDisplayLabel} › ${sousDomaineName}`,
        name: sousDomaineName,
        categoryName: categoryDisplayLabel,
        categoryId: category.id,
        sousDomaineId: sousDomaine.id,
      })

      const competenceDonnees = sousDomaine.competenceDonnees ?? []
      if (competenceDonnees.length > 0) {
        // New format: type d'ouvrage entries nested under each competence.
        for (const competenceDonnee of competenceDonnees) {
          for (const typeOuvrage of competenceDonnee.typeOuvrages ?? []) {
            const typeOuvrageName = libelleDynamique(
              typeOuvrage.typeOuvrageLib,
              typeOuvrage.typeOuvrageLibEn,
            )
            items.push({
              type: 'typeOuvrage',
              id: `typeouvrage-${sousDomaine.id}-${typeOuvrage.typId}`,
              displayLabel: `${sousDomaineName} › ${typeOuvrageName}`,
              name: typeOuvrageName,
              categoryName: sousDomaineName,
              categoryId: category.id,
              sousDomaineId: sousDomaine.id,
            })
          }
        }
      } else {
        // Old format: donnees directly under the sous-domaine.
        for (const donnee of sousDomaine.donnees ?? []) {
          const donneeName =
            libelleDynamique(donnee.name ?? donnee.nameFr, donnee.nameEn) || String(donnee.id)
          items.push({
            type: 'donnee',
            id: `donnee-${sousDomaine.id}-${donnee.id}`,
            displayLabel: `${sousDomaineName} › ${donneeName}`,
            name: donneeName,
            categoryName: sousDomaineName,
            categoryId: category.id,
            sousDomaineId: sousDomaine.id,
          })
        }
      }
    }
  }

  for (const row of infrastructureAttributeValues.value) {
    if (row.pkFkIftId == null) continue
    if (row.aglStatut === AGL_STATUT_REJECTED) continue
    const infraName = row.infrastructureName ?? String(row.pkFkIftId)
    const attributName = row.attributName ?? ''
    const valueText = formatAttributeValueText(row)
    if (!attributName && !valueText) continue
    items.push({
      type: 'infrastructure',
      id: `infrastructure-${row.pkFkIftId}-${row.pkFkAtgId ?? ''}`,
      displayLabel: [infraName, attributName, valueText].filter(Boolean).join(' '),
      name: infraName,
      categoryName:
        [attributName, valueText].filter(Boolean).join(': ') ||
        t('myMap.rightSidebar.searchResultTypeInfrastructure', 'Infrastructure'),
      categoryId: -1,
      infrastructureId: row.pkFkIftId,
    })
  }

  return items
})

function getSearchItemLabel(item: GeoportalSearchItem | null): string {
  return item?.displayLabel ?? ''
}

function onSearchSelect(value: string | GeoportalSearchItem | null) {
  if (value == null) return
  const id = typeof value === 'string' ? value : value.id
  const item = searchItems.value.find((i) => i.id === id)
  if (!item) return
  selectedSearchValue.value = null

  if (item.type === 'infrastructure' && item.infrastructureId != null) {
    mapStore.openInfrastructureFicheModal(item.infrastructureId, item.name)
    void resolveCompetenceIdForInfrastructure(item.infrastructureId)
    return
  }

  expandedCategoryId.value = item.categoryId
  pendingSousDomaineId.value = item.sousDomaineId ?? null
  mapStore.rightSidebarPanelVariant = 'osm'
  mapStore.activeOsmCategory.rightPanel.active = true
  mapStore.activeOsmCategory.rightPanel.categoryId = item.categoryId
  mapStore.rightSidebarPanelUnfolded = true
}

/** Extracts the type d'ouvrage code from an infrastructure API record (same field fallbacks as InfrastructureFicheContent). */
function getTypeOuvrageCode(infra: Record<string, unknown> | null): string | undefined {
  if (!infra) return undefined
  const raw =
    infra.ift_type_code ??
    infra.iftTypeCode ??
    (infra.typeOuvrage && typeof infra.typeOuvrage === 'object'
      ? ((infra.typeOuvrage as Record<string, unknown>).code ??
        (infra.typeOuvrage as Record<string, unknown>).libelle)
      : infra.type_ouvrage)
  return raw != null ? String(raw).trim() : undefined
}

/**
 * Once the fiche modal is opened from a search result, resolves and attaches the competenceId
 * (unknown at search time — only the type d'ouvrage code is), so the fiche can find the right
 * attribut definitions to display instead of showing "no attributes".
 */
async function resolveCompetenceIdForInfrastructure(infrastructureId: string | number): Promise<void> {
  try {
    const infra = await InfrastructureService.getById(infrastructureId)
    if (mapStore.infrastructureFicheModalId !== infrastructureId) return
    const typeCode = getTypeOuvrageCode(infra as Record<string, unknown>)
    if (!typeCode) return
    const competenceId = AtlasCategoriesService.findCompetenceIdForTypeOuvrageCode(typeCode)
    if (competenceId == null) return
    if (mapStore.infrastructureFicheModalId !== infrastructureId) return
    mapStore.openInfrastructureFicheModal(infrastructureId, mapStore.infrastructureFicheModalAttributeTitle ?? undefined, competenceId)
  } catch {
    // Fiche content will show "no attributes" — acceptable fallback, no need to surface an error.
  }
}

function onCategoryToggle(categoryId: number, expanded: boolean): void {
  if (!expanded) {
    if (expandedCategoryId.value === categoryId) {
      expandedCategoryId.value = null
    }
    return
  }
  expandedCategoryId.value = categoryId
  mapStore.activeOsmCategory.rightPanel.active = true
  mapStore.activeOsmCategory.rightPanel.categoryId = categoryId
}
</script>

<style lang="scss">
.SidebarSubcategoriesPanel {
  width: 0;
  min-width: 0;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  transition:
    min-width 0.2s ease-in-out,
    width 0.2s ease-in-out;

  &--unfolded {
    /* Matches the left sidebar's content panel width. */
    width: 23.5rem;
    min-width: 23.5rem;
  }
}

.SidebarSubcategoriesPanel__inner {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 23.5rem;
  min-width: 0;
  min-height: 0;
  /* No top padding here: it would scroll away inside the scrollport, leaving a gap above the
     sticky search bar through which scrolled-past domain rows could still peek. The search bar
     supplies its own top padding instead (see below), so it always starts flush at scroll offset 0. */
  padding: 0 0.9rem 1.5rem;
  overflow-y: auto;
  max-height: calc(100vh - var(--mymap-header-h));
}

.SidebarSubcategoriesPanel__search {
  position: sticky;
  top: 0;
  z-index: 5;
  /* Cancel the container's side padding and re-add it here so this opaque band spans edge-to-edge
     and fully covers anything scrolled underneath — no sliver of a domain row can show above it. */
  margin: 0 -0.9rem 0.7rem;
  padding: 0.9rem 0.9rem 0.35rem;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-theme-main-blue), 0.08);
}

.SidebarSubcategoriesPanel__searchResult {
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
}

.SidebarSubcategoriesPanel__searchResultLabel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.SidebarSubcategoriesPanel__searchResultName {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.SidebarSubcategoriesPanel__searchResultCategory {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-on-surface-variant));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.SidebarSubcategoriesPanel__hint {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
  padding: 0.5rem 0;
}
</style>
